import React from "react";
import { Modal, Button } from "react-bootstrap";
import {
  AlertTriangle,
  Camera,
  Lock,
  Maximize,
  MonitorPlay,
  Unlock,
} from "lucide-react";
import Toggle from "./Toggle";
import type { TestCategory } from "../../_services/TestCategoryService";

/**
 * Grouped settings tabs for the category detail view.
 *
 * Tabs are organised by admin intent, not by storage location — a tab may mix
 * `test_category_master` columns (form/setField/toggleField) with `Config`
 * JSON keys (config/setConfig). Field selection is grounded in a
 * key-frequency audit of all 86 categories across the `triangles` (55) and
 * `rvlh` (31) tenants — value types are identical in both.
 *
 * Storage cheat-sheet:
 *   form.*               -> dedicated DB columns
 *   config.testConfig.*  -> Config JSON: player behaviour keys
 *   config.* (top level) -> Config JSON: result-viewer, populate,
 *                           needsManualEvaluation, randomisedChoices, ...
 *
 * NOTE: `screenProctoring` is a newer browser-native feature not yet present
 * in any tenant row; `webCamProctoring` is the mechanism in active use.
 */

export interface GroupTabProps {
  form: TestCategory;
  setField: (key: keyof TestCategory, value: any) => void;
  toggleField: (key: keyof TestCategory) => void;
  config: any;
  setConfig: (updater: (prev: any) => any) => void;
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

const SwitchRow: React.FC<{
  label: string;
  hint?: string;
  checked: boolean;
  onChange: () => void;
}> = ({ label, hint, checked, onChange }) => (
  <div className="tcs-switch-row">
    <span className="tcs-switch-label">
      {label}
      {hint && <span className="tcs-switch-hint">{hint}</span>}
    </span>
    <Toggle checked={checked} onChange={onChange} aria-label={label} />
  </div>
);

const NumRow: React.FC<{
  label: string;
  hint?: string;
  value: number;
  onChange: (n: number) => void;
}> = ({ label, hint, value, onChange }) => (
  <div className="tcs-switch-row">
    <span className="tcs-switch-label">
      {label}
      {hint && <span className="tcs-switch-hint">{hint}</span>}
    </span>
    <input
      type="number"
      className="tcs-input"
      style={{ width: 100 }}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  </div>
);

const Field: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="tcs-field">
    <label className="tcs-label">{label}</label>
    {hint && <p className="tcs-hint">{hint}</p>}
    {children}
  </div>
);

const SubHeading: React.FC<{ title: string; note?: string }> = ({
  title,
  note,
}) => (
  <div className="tcs-subheading">
    <h6>{title}</h6>
    {note && <p className="tcs-hint">{note}</p>}
  </div>
);

/** Config helpers shared by the tabs. */
const useConfigSetters = (
  config: any,
  setConfig: GroupTabProps["setConfig"],
) => {
  const tc = config?.testConfig || {};

  const setTc = (key: string, value: any) =>
    setConfig((prev: any) => ({
      ...prev,
      testConfig: { ...(prev?.testConfig || {}), [key]: value },
    }));

  const setTop = (key: string, value: any) =>
    setConfig((prev: any) => ({ ...prev, [key]: value }));

  const setTopNested = (key: string, field: string, value: any) =>
    setConfig((prev: any) => ({
      ...prev,
      [key]: { ...(prev?.[key] || {}), [field]: value },
    }));

  return { tc, setTc, setTop, setTopNested };
};

// ---------------------------------------------------------------------------
// Test Taking — everything before and during the attempt
// ---------------------------------------------------------------------------

export const TestTakingTab: React.FC<GroupTabProps> = ({
  form,
  setField,
  toggleField,
  config,
  setConfig,
}) => {
  const { tc, setTc, setTop, setTopNested } = useConfigSetters(
    config,
    setConfig,
  );
  const populate = config?.populate || {};

  return (
    <div>
      <SubHeading
        title="Before the Test"
        note="Eligibility checks and consent shown before the student can start."
      />
      <SwitchRow
        label="Check Payment Status Before Start"
        checked={!!tc.checkForPaymentStatusBeforeStart}
        onChange={() =>
          setTc(
            "checkForPaymentStatusBeforeStart",
            !tc.checkForPaymentStatusBeforeStart,
          )
        }
      />
      <SwitchRow
        label="Check Attendance Status Before Start"
        checked={!!tc.checkForAttendanceStatusBeforeStart}
        onChange={() =>
          setTc(
            "checkForAttendanceStatusBeforeStart",
            !tc.checkForAttendanceStatusBeforeStart,
          )
        }
      />
      <SwitchRow
        label="Test Level Password Check"
        checked={!!tc.testLevelPasswordCheck}
        onChange={() =>
          setTc("testLevelPasswordCheck", !tc.testLevelPasswordCheck)
        }
      />
      <SwitchRow
        label="Student Level Password Check"
        checked={!!tc.studentLevelPasswordCheck}
        onChange={() =>
          setTc("studentLevelPasswordCheck", !tc.studentLevelPasswordCheck)
        }
      />
      <Field
        label="Test Consent Message"
        hint="Shown to the student before the test begins."
      >
        <textarea
          className="tcs-textarea"
          rows={2}
          value={tc.testConsent || ""}
          onChange={(e) => setTc("testConsent", e.target.value)}
        />
      </Field>

      <SubHeading title="Attempts & Timing" />
      <div className="tcs-switch-row">
        <span className="tcs-switch-label">Max Attempts</span>
        <select
          className="tcs-select"
          style={{ width: 100 }}
          value={tc.maxAttempts ?? 1}
          onChange={(e) => setTc("maxAttempts", Number(e.target.value))}
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <NumRow
        label="Grace Minutes"
        value={tc.graceMinutes ?? 0}
        onChange={(n) => setTc("graceMinutes", n)}
      />
      <SwitchRow
        label="Allow Admin To Enable Retake"
        checked={!!form.allowAdminToEnableRetake}
        onChange={() => toggleField("allowAdminToEnableRetake")}
      />
      <SwitchRow
        label="Hard Stop After Start"
        checked={!!tc.hardStopAfterStart}
        onChange={() => setTc("hardStopAfterStart", !tc.hardStopAfterStart)}
      />

      <SubHeading title="During the Test" />
      <SwitchRow
        label="Disable Navigation Panel"
        checked={!!tc.disableNavigationPanel}
        onChange={() =>
          setTc("disableNavigationPanel", !tc.disableNavigationPanel)
        }
      />
      <SwitchRow
        label="Mark For Review"
        checked={!!tc.markForReview}
        onChange={() => setTc("markForReview", !tc.markForReview)}
      />
      <SwitchRow
        label="Disable Finish Button"
        checked={!!tc.disableFinishButton}
        onChange={() => setTc("disableFinishButton", !tc.disableFinishButton)}
      />
      <SwitchRow
        label="Premature Termination"
        checked={!!form.prematureTermination}
        onChange={() => toggleField("prematureTermination")}
      />
      <SwitchRow
        label="Redirect Online Test To iLearn"
        checked={!!tc.redirectOnlineTestToiLearn}
        onChange={() =>
          setTc("redirectOnlineTestToiLearn", !tc.redirectOnlineTestToiLearn)
        }
      />
      <SwitchRow
        label="Randomised Choices"
        hint="Shuffle answer options for each student."
        checked={!!config?.randomisedChoices}
        onChange={() => setTop("randomisedChoices", !config?.randomisedChoices)}
      />
      <SwitchRow
        label="On-Screen Calculator"
        hint="Calculator keyword list lives in Raw JSON (Config.calculator)."
        checked={!!tc.calculator}
        onChange={() => setTc("calculator", !tc.calculator)}
      />
      <div className="tcs-switch-row">
        <span className="tcs-switch-label">Question Number Display Format</span>
        <select
          className="tcs-select"
          style={{ width: 130 }}
          value={form.questionNumberDisplayFormat ?? 1}
          onChange={(e) =>
            setField("questionNumberDisplayFormat", Number(e.target.value))
          }
        >
          <option value={1}>1 (Default)</option>
          <option value={2}>2</option>
          <option value={3}>3</option>
        </select>
      </div>

      <SubHeading title="Authoring & Advanced" />
      <SwitchRow
        label="Allow Adding Test Question Rules"
        checked={!!form.allowAddingOfTestQuestionRules}
        onChange={() => toggleField("allowAddingOfTestQuestionRules")}
      />
      <SwitchRow
        label="Course Option Based"
        checked={!!form.courseOptionBased}
        onChange={() => toggleField("courseOptionBased")}
      />
      <SwitchRow
        label="Auto-Populate Responses"
        hint="Testing aid — auto-fills every response with the selected option. Keep off in production."
        checked={!!populate.enableStatus}
        onChange={() =>
          setTopNested("populate", "enableStatus", !populate.enableStatus)
        }
      />
      {!!populate.enableStatus && (
        <NumRow
          label="Populate Option"
          value={populate.option ?? 1}
          onChange={(n) => setTopNested("populate", "option", n)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Proctoring & Security — every proctoring mechanism in one place
// ---------------------------------------------------------------------------

// Webcam image-analysis rules. When set on a category, nodefork publishes
// them per-test (rules/<tenant>/<testId>.json in the proctoring bucket) as a
// student launches the test, and the proctorDynamodb lambda uses them instead
// of the tenant-wide proctoring.json defaults.
const DEFAULT_ANALYSIS_RULES = {
  AuthenticateFace: {
    enabled: true,
    startIndex: 1,
    skip: 0,
    blockAfterIndex: 0,
    checkForLastNImages: 1,
  },
  SecondFace: { enabled: true, startIndex: 2, skip: 2 },
  Mobile: { enabled: false, startIndex: 1, skip: 4 },
  Emotions: { enabled: false, startIndex: 1, skip: 4 },
  blockOnViolation: { enabled: false, blockAfterNViolation: 3 },
};

const ANALYSIS_METRICS = [
  {
    key: "AuthenticateFace",
    label: "Face Match",
    hint: "Compares each webcam capture against the student's profile photo.",
  },
  {
    key: "SecondFace",
    label: "Second Person Detection",
    hint: "Flags captures where more than one face is visible.",
  },
  {
    key: "Mobile",
    label: "Mobile / Device Detection",
    hint: "Flags phones, tablets, headphones or earphones in the frame.",
  },
  {
    key: "Emotions",
    label: "Emotion Analysis",
    hint: "Records the student's mood per capture for the session review.",
  },
] as const;

const AnalysisRulesEditor: React.FC<{
  rules: any;
  setRules: (next: any) => void;
}> = ({ rules, setRules }) => {
  const setRule = (metric: string, field: string, value: any) =>
    setRules({
      ...rules,
      [metric]: { ...(rules?.[metric] || {}), [field]: value },
    });

  return (
    <>
      <SubHeading
        title="Image Analysis Rules"
        note="Which AI checks run on the webcam captures and how often. Rules apply from the moment a student launches a test in this category; without custom rules the tenant-wide defaults are used."
      />
      {ANALYSIS_METRICS.map(({ key, label, hint }) => {
        const rule = rules?.[key] || {};
        return (
          <React.Fragment key={key}>
            <SwitchRow
              label={label}
              hint={hint}
              checked={!!rule.enabled}
              onChange={() => setRule(key, "enabled", !rule.enabled)}
            />
            {!!rule.enabled && (
              <div style={{ paddingLeft: 24 }}>
                <NumRow
                  label="First image to check"
                  hint="Captures before this one are not analysed."
                  value={rule.startIndex ?? 1}
                  onChange={(n) => setRule(key, "startIndex", n)}
                />
                <NumRow
                  label="Images skipped between checks"
                  hint="0 = analyse every capture; 4 = analyse every 5th."
                  value={rule.skip ?? 0}
                  onChange={(n) => setRule(key, "skip", n)}
                />
                {key === "AuthenticateFace" && (
                  <>
                    <NumRow
                      label="Block after image #"
                      hint="Face-match failures only start blocking after this capture (0 = immediately)."
                      value={rule.blockAfterIndex ?? 0}
                      onChange={(n) => setRule(key, "blockAfterIndex", n)}
                    />
                    <NumRow
                      label="Check last N images before blocking"
                      hint="The match must fail across this many recent captures before the student is blocked."
                      value={rule.checkForLastNImages ?? 1}
                      onChange={(n) => setRule(key, "checkForLastNImages", n)}
                    />
                  </>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}
      <SwitchRow
        label="Auto-block on Violations"
        hint="Automatically blocks the attempt once total violations across all checks reach the threshold."
        checked={!!rules?.blockOnViolation?.enabled}
        onChange={() =>
          setRule(
            "blockOnViolation",
            "enabled",
            !rules?.blockOnViolation?.enabled,
          )
        }
      />
      {!!rules?.blockOnViolation?.enabled && (
        <div style={{ paddingLeft: 24 }}>
          <NumRow
            label="Block after N violations"
            value={rules?.blockOnViolation?.blockAfterNViolation ?? 3}
            onChange={(n) =>
              setRule("blockOnViolation", "blockAfterNViolation", n)
            }
          />
        </div>
      )}
    </>
  );
};

export const ProctoringTab: React.FC<GroupTabProps> = ({
  form,
  toggleField,
  config,
  setConfig,
}) => {
  const { tc, setTc } = useConfigSetters(config, setConfig);

  const setWebcam = (field: string, value: any) =>
    setTc("webCamProctoring", {
      ...(tc.webCamProctoring || {}),
      [field]: value,
    });

  const setScreen = (field: string, value: any) =>
    setTc("screenProctoring", {
      ...(tc.screenProctoring || {}),
      [field]: value,
    });

  const webcamOn = !!tc.webCamProctoring?.enabled;
  const screenOn = !!tc.screenProctoring?.enabled;

  // Lockdown is one choice, not stacked toggles: SEB (native app) wins over
  // Lite (browser JS) — inside SEB the browser is already a kiosk, so the JS
  // fullscreen/tab checks must never run there. Legacy configs written before
  // the split expressed Lite as screenProctoring.softLockdown (default-on when
  // screen proctoring was enabled) — read that shape, but always write the
  // normalized one.
  const sebOn = !!tc.lockdownBrowser;
  const legacyLite =
    !!tc.screenProctoring?.enabled &&
    tc.screenProctoring?.softLockdown !== false;
  const liteOn = !sebOn && (tc.liteLockdown?.enabled === true || legacyLite);
  const lockdownMode: "off" | "seb" | "lite" = sebOn
    ? "seb"
    : liteOn
      ? "lite"
      : "off";

  // Applies a lockdown mode atomically, optionally flipping Screen Recording
  // in the same update (used by the SEB-conflict modal so the two never end
  // up combined). Keeps the legacy softLockdown flag in sync for
  // already-deployed players: they activate soft lockdown whenever screen
  // proctoring is on unless the flag is explicitly false.
  const applyLockdown = (
    mode: "off" | "seb" | "lite",
    opts?: { recording?: boolean },
  ) =>
    setConfig((prev: any) => {
      const prevTc = prev?.testConfig || {};
      const nextTc: any = { ...prevTc, lockdownBrowser: mode === "seb" };
      if (mode === "lite") {
        nextTc.liteLockdown = {
          maxTabSwitchViolations: 5,
          maxFullscreenExitViolations: 3,
          ...(prevTc.liteLockdown || {}),
          enabled: true,
        };
      } else if (prevTc.liteLockdown) {
        nextTc.liteLockdown = { ...prevTc.liteLockdown, enabled: false };
      }
      if (prevTc.screenProctoring || opts?.recording !== undefined) {
        nextTc.screenProctoring = {
          ...(prevTc.screenProctoring || {}),
          ...(opts?.recording !== undefined ? { enabled: opts.recording } : {}),
          softLockdown: mode === "lite",
        };
      }
      return { ...prev, testConfig: nextTc };
    });

  // SEB and browser screen capture are mutually exclusive (the share picker
  // can't open inside SEB), so the two levers guard each other with a
  // conflict modal instead of a fine-print note nobody reads.
  const [sebConflict, setSebConflict] = React.useState<
    null | "selectSeb" | "enableRecording"
  >(null);

  const handleLockdownSelect = (mode: "off" | "seb" | "lite") => {
    if (mode === lockdownMode) return;
    if (mode === "seb" && screenOn) {
      setSebConflict("selectSeb");
      return;
    }
    applyLockdown(mode);
  };

  const liteLimit = (field: string, fallback: number) =>
    tc.liteLockdown?.[field] ?? tc.screenProctoring?.[field] ?? fallback;

  const setLiteLimit = (field: string, value: number) =>
    setConfig((prev: any) => {
      const prevTc = prev?.testConfig || {};
      const nextTc: any = {
        ...prevTc,
        liteLockdown: {
          enabled: true,
          ...(prevTc.liteLockdown || {}),
          [field]: value,
        },
      };
      // Mirror for already-deployed players, which read the thresholds from
      // screenProctoring.
      if (prevTc.screenProctoring) {
        nextTc.screenProctoring = {
          ...prevTc.screenProctoring,
          [field]: value,
        };
      }
      return { ...prev, testConfig: nextTc };
    });

  const summary = [
    {
      icon: Camera,
      name: "Webcam Proctoring",
      on: webcamOn,
      state: webcamOn ? "Enabled" : "Off",
    },
    {
      icon: Lock,
      name: "Lockdown",
      on: lockdownMode !== "off",
      state:
        lockdownMode === "seb"
          ? "SEB (Native)"
          : lockdownMode === "lite"
            ? "Lite (Browser)"
            : "Off",
    },
    {
      icon: MonitorPlay,
      name: "Screen Recording",
      on: screenOn,
      state: screenOn ? "Enabled" : "Off",
    },
  ];

  return (
    <div>
      <div className="tcs-proctor-summary">
        {summary.map(({ icon: Icon, name, on, state }) => (
          <div key={name} className={`tcs-proctor-card ${on ? "on" : ""}`}>
            <span className="tcs-proctor-icon">
              <Icon size={17} />
            </span>
            <span>
              <span className="tcs-proctor-name">{name}</span>
              <span className="tcs-proctor-state">{state}</span>
            </span>
          </div>
        ))}
      </div>

      <SubHeading title="General" />
      <SwitchRow
        label="Proctored Category"
        hint="Master flag — marks every test in this category as proctored."
        checked={!!form.proctored}
        onChange={() => toggleField("proctored")}
      />
      <SwitchRow
        label="Ignore Suspicious Activity Flag"
        hint="Suspicious-activity events are logged but do not block the student."
        checked={!!tc.ignoreSuspiciousActivityFlag}
        onChange={() =>
          setTc(
            "ignoreSuspiciousActivityFlag",
            !tc.ignoreSuspiciousActivityFlag,
          )
        }
      />

      <SubHeading
        title="Webcam Proctoring"
        note="Server-side image/video capture during the test. This is the proctoring mechanism in active use across tenants."
      />
      <SwitchRow
        label="Enable Webcam Proctoring"
        checked={webcamOn}
        onChange={() => setWebcam("enabled", !webcamOn)}
      />
      {webcamOn && (
        <>
          <SwitchRow
            label="Continue Without Profile Photo"
            hint="Proctoring matches each student against their profile photo. A student with no photo on record is blocked at launch and told to contact the Exam Department — turn this on to let them take the test anyway."
            checked={!!tc.webCamProctoring?.continueWithoutProfilePhoto}
            onChange={() =>
              setWebcam(
                "continueWithoutProfilePhoto",
                !tc.webCamProctoring?.continueWithoutProfilePhoto,
              )
            }
          />
          <SwitchRow
            label="Custom Image Analysis Rules"
            hint="Override the tenant-wide analysis defaults for this category. Off = tests in this category follow the shared proctoring.json rules."
            checked={!!tc.webCamProctoring?.analysisRules}
            onChange={() =>
              setWebcam(
                "analysisRules",
                tc.webCamProctoring?.analysisRules
                  ? undefined
                  : DEFAULT_ANALYSIS_RULES,
              )
            }
          />
          {!!tc.webCamProctoring?.analysisRules && (
            <AnalysisRulesEditor
              rules={tc.webCamProctoring.analysisRules}
              setRules={(next) => setWebcam("analysisRules", next)}
            />
          )}
        </>
      )}
      {/* captureImageFrequency / captureImageEndPoint / captureVideoEndPoint /
          captureType are legacy keys from the old Java-era player — nothing in
          the current stack reads them (the capture iframe receives its
          parameters via URL query only). Deliberately not rendered; existing
          values are preserved untouched in the JSON and stay editable from the
          Raw JSON tab. */}

      <SubHeading
        title="Lockdown"
        note="Locks the student into the exam. SEB requires the Safe Exam Browser app installed on the student's machine; Lite enforces fullscreen + tab-switch detection in the normal browser — nothing to install."
      />
      <div className="tcs-mode-seg" role="radiogroup" aria-label="Lockdown Mode">
        {(
          [
            {
              key: "off",
              icon: Unlock,
              title: "Off",
              desc: "No lockdown — students take the test in a normal browser window.",
            },
            {
              key: "seb",
              icon: Lock,
              title: "SEB (Native)",
              desc: "Strongest option. Requires the Safe Exam Browser app installed on the student's machine.",
            },
            {
              key: "lite",
              icon: Maximize,
              title: "Lite (Browser)",
              desc: "Fullscreen + tab-switch detection in the normal browser. Nothing to install.",
            },
          ] as const
        ).map(({ key, icon: Icon, title, desc }) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={lockdownMode === key}
            className={`tcs-mode-option ${lockdownMode === key ? "selected" : ""}`}
            onClick={() => handleLockdownSelect(key)}
          >
            <span className="tcs-mode-icon">
              <Icon size={16} />
            </span>
            <span>
              <span className="tcs-mode-title">{title}</span>
              <span className="tcs-mode-desc">{desc}</span>
            </span>
          </button>
        ))}
      </div>
      {lockdownMode === "lite" && (
        <>
          <NumRow
            label="Max Tab Switch Violations"
            hint="Attempt is flagged when the student switches tabs this many times."
            value={liteLimit("maxTabSwitchViolations", 5)}
            onChange={(n) => setLiteLimit("maxTabSwitchViolations", n)}
          />
          <NumRow
            label="Max Fullscreen Exit Violations"
            hint="Attempt is flagged when the student leaves fullscreen this many times."
            value={liteLimit("maxFullscreenExitViolations", 3)}
            onChange={(n) => setLiteLimit("maxFullscreenExitViolations", n)}
          />
        </>
      )}

      <SubHeading
        title="Screen Recording"
        note='Records the student’s screen (and optionally webcam) during the test using the browser’s own capture — nothing to install, but the student must click "Allow" and choose "Entire Screen" when the browser asks. Recordings upload in chunks and are reviewed under Proctoring → Session Review. Fullscreen/tab lockdown is configured in the Lockdown section above, independently of recording.'
      />
      {sebOn && screenOn && (
        <div className="tcs-conflict-banner">
          <AlertTriangle size={15} />
          <span>
            <b>Unsupported combination:</b> Screen Recording is on together
            with SEB lockdown. The screen-share picker cannot open inside the
            Safe Exam Browser, so recording will fail and students may be
            blocked at the sharing step. Turn Screen Recording off, or switch
            Lockdown to Lite.
          </span>
        </div>
      )}
      <SwitchRow
        label="Enable Screen Recording"
        checked={screenOn}
        onChange={() => {
          if (!screenOn && lockdownMode === "seb") {
            setSebConflict("enableRecording");
            return;
          }
          setScreen("enabled", !screenOn);
        }}
      />
      {screenOn && (
        <>
          <SwitchRow
            label="Show Screen-Share Setup Instructions"
            hint='Before the browser’s permission prompt, students see a short checklist: click Allow, pick "Entire Screen", use a single monitor, don’t stop sharing mid-exam.'
            checked={tc.screenProctoring?.setupInstructions !== false}
            onChange={() =>
              setScreen(
                "setupInstructions",
                tc.screenProctoring?.setupInstructions === false,
              )
            }
          />
          <SwitchRow
            label="Record Screen"
            checked={tc.screenProctoring?.recordScreen !== false}
            onChange={() =>
              setScreen(
                "recordScreen",
                tc.screenProctoring?.recordScreen === false,
              )
            }
          />
          <SwitchRow
            label="Record Webcam (Backup)"
            hint="Continuous raw webcam video on the same recording pipeline, for human review alongside the screen footage. Independent of Webcam Proctoring above (AI-analyzed snapshots)."
            checked={!!tc.screenProctoring?.recordWebcam}
            onChange={() =>
              setScreen("recordWebcam", !tc.screenProctoring?.recordWebcam)
            }
          />
          <NumRow
            label="Chunk Interval (seconds)"
            value={tc.screenProctoring?.chunkIntervalSeconds ?? 30}
            onChange={(n) => setScreen("chunkIntervalSeconds", n)}
          />
        </>
      )}

      <Modal
        show={sebConflict !== null}
        onHide={() => setSebConflict(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {sebConflict === "selectSeb"
              ? "Screen Recording conflicts with SEB"
              : "SEB lockdown is on"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.9rem" }}>
          {sebConflict === "selectSeb" ? (
            <>
              Screen Recording is enabled on this category, but the browser's
              screen-share picker cannot open inside the Safe Exam Browser —
              recording would fail and students could get stuck at the sharing
              step. Switching to SEB will turn Screen Recording off.
            </>
          ) : (
            <>
              Screen Recording cannot run inside the Safe Exam Browser — the
              screen-share picker never opens there, so students would be
              blocked at the sharing step. To record, switch Lockdown to Lite
              (fullscreen + tab lock in the normal browser, nothing to
              install).
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSebConflict(null)}>
            Cancel
          </Button>
          {sebConflict === "selectSeb" ? (
            <Button
              variant="primary"
              onClick={() => {
                applyLockdown("seb", { recording: false });
                setSebConflict(null);
              }}
            >
              Turn off Recording &amp; use SEB
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                applyLockdown("lite", { recording: true });
                setSebConflict(null);
              }}
            >
              Switch to Lite &amp; enable Recording
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Submission & Uploads
// ---------------------------------------------------------------------------

export const SubmissionTab: React.FC<GroupTabProps> = ({
  config,
  setConfig,
}) => {
  const { tc, setTc } = useConfigSetters(config, setConfig);

  const formats: string[] = Array.isArray(tc.validUploadFormats)
    ? tc.validUploadFormats
    : [];

  return (
    <div>
      <SubHeading title="Submission Messages" />
      <Field
        label="Auto Submit Message"
        hint="Shown when time runs out and the test submits itself."
      >
        <textarea
          className="tcs-textarea"
          rows={2}
          value={tc.autoSubmit || ""}
          onChange={(e) => setTc("autoSubmit", e.target.value)}
        />
      </Field>
      <Field
        label="Student Submit Message"
        hint="Shown when the student submits the test themselves."
      >
        <textarea
          className="tcs-textarea"
          rows={2}
          value={tc.studentSubmit || ""}
          onChange={(e) => setTc("studentSubmit", e.target.value)}
        />
      </Field>

      <SubHeading
        title="Response Sync (RDS)"
        note="Controls how responses are synced to the result data store."
      />
      <SwitchRow
        label="Auto Submit To RDS"
        checked={!!tc.autoSubmitToRDS}
        onChange={() => setTc("autoSubmitToRDS", !tc.autoSubmitToRDS)}
      />
      <SwitchRow
        label="Init RDS On Test Start"
        checked={!!tc.initRDSonTestStart}
        onChange={() => setTc("initRDSonTestStart", !tc.initRDSonTestStart)}
      />

      <SubHeading title="Attachments" />
      <SwitchRow
        label="Allow Attachment Upload After Test"
        checked={!!tc.allowAttachmentUploadAfterTest}
        onChange={() =>
          setTc(
            "allowAttachmentUploadAfterTest",
            !tc.allowAttachmentUploadAfterTest,
          )
        }
      />
      <SwitchRow
        label="Allow Attachment Upload Per Text Question"
        checked={!!tc.allowAttachmentUploadForEachTextQuestionResponse}
        onChange={() =>
          setTc(
            "allowAttachmentUploadForEachTextQuestionResponse",
            !tc.allowAttachmentUploadForEachTextQuestionResponse,
          )
        }
      />
      <Field
        label="Valid Upload Formats"
        hint="Comma-separated file extensions, e.g. pdf, docx, mp4, mp3, xls"
      >
        <input
          className="tcs-input"
          value={formats.join(", ")}
          onChange={(e) =>
            setTc(
              "validUploadFormats",
              e.target.value
                .split(",")
                .map((s) => s.trim().toLowerCase())
                .filter(Boolean),
            )
          }
        />
      </Field>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Results & Analysis
// ---------------------------------------------------------------------------

export const ResultsTab: React.FC<GroupTabProps> = ({
  form,
  toggleField,
  config,
  setConfig,
}) => {
  const { tc, setTc, setTop, setTopNested } = useConfigSetters(
    config,
    setConfig,
  );
  const rv = config?.["result-viewer"] || {};

  return (
    <div>
      <SubHeading
        title="Result Viewer"
        note="Controls how students see their result after submission."
      />
      <Field label="Result Declaration Message">
        <textarea
          className="tcs-textarea"
          rows={2}
          value={tc.resultDeclarationMessage || ""}
          onChange={(e) => setTc("resultDeclarationMessage", e.target.value)}
        />
      </Field>
      <div className="tcs-switch-row">
        <span className="tcs-switch-label">Mode</span>
        <select
          className="tcs-select"
          style={{ width: 100 }}
          value={String(rv.mode ?? "1")}
          onChange={(e) =>
            setTopNested("result-viewer", "mode", e.target.value)
          }
        >
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
        </select>
      </div>
      <SwitchRow
        label="Show Details"
        checked={!!rv.showDetails}
        onChange={() =>
          setTopNested("result-viewer", "showDetails", !rv.showDetails)
        }
      />
      <NumRow
        label="Show Details (legacy numeric flag)"
        value={config?.showDetails ?? 0}
        onChange={(n) => setTop("showDetails", n)}
      />

      <SubHeading
        title="Student-Visible Analysis"
        note="What the student can open alongside their result."
      />
      <SwitchRow
        label="Display Question Analysis"
        checked={!!form.displayQuestionAnalysis}
        onChange={() => toggleField("displayQuestionAnalysis")}
      />
      <SwitchRow
        label="Display Solution"
        checked={!!form.displaySolution}
        onChange={() => toggleField("displaySolution")}
      />
      <SwitchRow
        label="Question Type Analysis"
        checked={!!form.questionTypeAnalysis}
        onChange={() => toggleField("questionTypeAnalysis")}
      />
      <SwitchRow
        label="Test Section Analysis"
        checked={!!form.testSectionAnalysis}
        onChange={() => toggleField("testSectionAnalysis")}
      />
      <SwitchRow
        label="Sectional Test Time Distribution"
        checked={!!form.sectionalTestTimeDistribution}
        onChange={() => toggleField("sectionalTestTimeDistribution")}
      />
      <SwitchRow
        label="Display Calculated Grade"
        checked={!!form.displayCalculatedGrade}
        onChange={() => toggleField("displayCalculatedGrade")}
      />
      <SwitchRow
        label="Don't Show Marks And Rank"
        checked={!!form.dontShowMarksAndRank}
        onChange={() => toggleField("dontShowMarksAndRank")}
      />

      <SubHeading title="Evaluation" />
      <SwitchRow
        label="Needs Manual Evaluation"
        hint="Routes submissions into the DVS manual-evaluation queue."
        checked={!!config?.needsManualEvaluation}
        onChange={() =>
          setTop("needsManualEvaluation", !config?.needsManualEvaluation)
        }
      />
      <SwitchRow
        label="DVS Evaluation — Show Student Name"
        checked={!!config?.DVSEvaluationShowStudentName}
        onChange={() =>
          setTop(
            "DVSEvaluationShowStudentName",
            !config?.DVSEvaluationShowStudentName,
          )
        }
      />
      <SwitchRow
        label="Remarks Allowed"
        checked={!!form.remarksAllowed}
        onChange={() => toggleField("remarksAllowed")}
      />
    </div>
  );
};
